import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	CanActivateChild,
	Router,
	RouterStateSnapshot,
	UrlTree,
} from '@angular/router';
import { AuthenticationService } from '@gt/_services/authentication.service';
import { Role } from '@para/shared/utils/para-roles.util';
import { isParaShellRole, paraDefaultPathForRole } from '../para-default-route.util';

@Injectable({ providedIn: 'root' })
export class ParaAuthGuard implements CanActivate, CanActivateChild {
	constructor(private auth: AuthenticationService, private router: Router) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
		return this.check(route, state);
	}

	canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
		return this.check(childRoute, state);
	}

	private check(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean | UrlTree {
		if (!this.auth.isAuthenticated()) {
			return this.router.parseUrl('/login');
		}

		const role = this.auth.role as Role | string | undefined;

		// Always enforce shell access; do not rely on route.data.roles alone (lazy children often omit it).
		if (!isParaShellRole(role)) {
			return this.router.parseUrl(paraDefaultPathForRole(role));
		}

		const allowedRoles = this.resolveAllowedRoles(route);
		if (allowedRoles?.length && !(allowedRoles as string[]).includes(role as string)) {
			return this.router.parseUrl(paraDefaultPathForRole(role));
		}

		return true;
	}

	/** Nearest `data.roles` on this snapshot or any ancestor/descendant (for lazy + nested routes). */
	private resolveAllowedRoles(route: ActivatedRouteSnapshot): Role[] | undefined {
		for (let cur: ActivatedRouteSnapshot | null = route; cur; cur = cur.parent) {
			const roles = cur.data?.roles as Role[] | undefined;
			if (roles?.length) return roles;
		}
		let cur: ActivatedRouteSnapshot | null = route;
		while (cur?.firstChild) {
			cur = cur.firstChild;
			const roles = cur.data?.roles as Role[] | undefined;
			if (roles?.length) return roles;
		}
		return undefined;
	}
}
